/* eslint-disable jsx-a11y/media-has-caption */

import React, { useState, useRef } from "react";
import { logEvent } from "firebase/analytics";

import "./InlinePlayableTawkPill.css";

import { Cloudinary } from "@cloudinary/url-gen";
import { trim } from "@cloudinary/url-gen/actions/videoEdit";
import { AdvancedVideo } from "@cloudinary/react";

import ReactAudioPlayer from "react-audio-player";
import { analytics } from "../../App/Analytics";

import {
  TawkPill,
  TawkPillConfig,
  demoProps1 as tawkPillDemoProps1,
} from "../TawkPill/TawkPill";

export interface EchoTimeRange {
  startTime: number;
  endTime: number;
}

export type CloudinaryVideoPayload = {
  kind: "CloudinaryVideoPayload";
  publicId: string;
};
export type AudioUrl = {
  kind: "AudioUrl";
  url: string;
};
export type PlayablePayload = CloudinaryVideoPayload | AudioUrl;

/* eslint-disable react/no-unused-prop-types */
export interface InlinePlayableTawkPillConfig {
  kind: "InlinePlayableTawkPillConfig";
  tawkPillConfig: TawkPillConfig;
  playablePayload: PlayablePayload;
  timeRange: EchoTimeRange | null;
  preExpandedAndAutoplaying?: boolean;
}
/* eslint-enable react/no-unused-prop-types */

export const inlinePlayableTawkPillConfigDemo1: InlinePlayableTawkPillConfig = {
  kind: "InlinePlayableTawkPillConfig",
  tawkPillConfig: tawkPillDemoProps1,
  playablePayload: {
    kind: "CloudinaryVideoPayload",
    publicId:
      "https://res.cloudinary.com/dfno6k3qf/video/upload/v1/tawkResources/3c16c2cb-39be-4bc6-a513-9e31fb3d1e70_7738D3E0-DEB1-4348-BAA1-3EF726EFDF7E/generatedVideos/burnedInSubtitles/720x720.mp4.mp4",
  },
  timeRange: null,
};

function _VideoComponent(
  cloudinaryVideoPayload: CloudinaryVideoPayload,
  timeRange: EchoTimeRange | null
): JSX.Element {
  const myCld = new Cloudinary({ cloud: { cloudName: "dfno6k3qf" } });
  const cloudinaryVideo = myCld.video(cloudinaryVideoPayload.publicId);

  if (timeRange) {
    cloudinaryVideo.videoEdit(
      trim().startOffset(timeRange.startTime).endOffset(timeRange.endTime)
    );
  }

  return (
    <div className="InlinePlayableVideoContainer">
      <AdvancedVideo cldVid={cloudinaryVideo} playsInline autoPlay controls />
    </div>
  );
}

function _AudioComponent(props: {
  audioPayload: AudioUrl;
  timeRange: EchoTimeRange | null;
}): JSX.Element {
  const audioPlayer = useRef<HTMLAudioElement>();
  const [readyToGo, setReadyToGo] = useState(false);

  if (readyToGo && audioPlayer.current && props.timeRange) {
    const startTime = Math.floor(props.timeRange.startTime);
    audioPlayer.current.currentTime = startTime;
  }

  return (
    <ReactAudioPlayer
      src={props.audioPayload.url}
      autoPlay
      controls
      ref={(element) => {
        audioPlayer.current = element?.audioEl.current ?? undefined;
        setReadyToGo(true);
      }}
    />
  );
}

function _PlayableComponent(config: InlinePlayableTawkPillConfig): JSX.Element {
  if (config.playablePayload.kind === "CloudinaryVideoPayload") {
    return _VideoComponent(config.playablePayload, config.timeRange);
  } else {
    const audioComponentProps = {
      audioPayload: config.playablePayload,
      timeRange: config.timeRange,
    };
    return <_AudioComponent {...audioComponentProps} />;
  }
}

export function InlinePlayableTawkPill(
  props: InlinePlayableTawkPillConfig
): JSX.Element {
  const [showVideo, setShowVideo] = useState(
    props.preExpandedAndAutoplaying ?? false
  );

  function toggleVideo() {
    const newShowVideoState = !showVideo;
    logEvent(analytics, "inline_playable_tawkpill_toggle_video", {
      new_show_video_state: newShowVideoState,
      pre_expanded_and_autoplaying: props.preExpandedAndAutoplaying,
      tawk_title: props.tawkPillConfig.tawkTitle,
      author_user_handle: props.tawkPillConfig.authorUserHandle,
    });
    return setShowVideo(() => newShowVideoState);
  }

  // console.log("is it or not");
  return (
    <div
      className="InlinePlayablePillStack"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "15px",
      }}
    >
      <button type="button" className="tawk-pill" onClick={toggleVideo}>
        <TawkPill {...props.tawkPillConfig} />
      </button>
      {showVideo ? _PlayableComponent(props) : null}
    </div>
  );
}
